'use client';

import { useState, type ReactNode } from 'react';
import type { SanityImage } from '../types';
import { tv } from '@app/ui/_lib/utils';
import { Media } from '../Media';
import type { noneLeftRightValues } from '../_lib/designTypesStore';
import { Text } from '../Text';
import { Container } from '../Container';
import { CalendlyCtaActionPopup, CtaAction, HubspotFormCtaAction } from '../CtaActions';
import { Button, ButtonLink } from '../Inputs/Button';

const styles = tv({
	slots: {
		base: '',
		contentBase: [
			'relative rounded-lg flex flex-col items-center bg-card-background-secondary border border-border-secondary overflow-hidden isolate',
			'md:px-responsive-5xl md:flex-row',
		],
		content: 'container-md py-responsive-7xl flex flex-col gap-responsive-sm items-center text-center',
		button: '',
	},
	variants: {
		textPosition: {
			none: {
				content: '',
				button: 'max-md:w-full',
			},
			left: {
				content: 'md:items-start md:text-left md:-order-1',
			},
			right: {
				content: 'md:items-start md:text-left',
			},
		},
	},
});

export type CTAProps = {
	className?: string;
	action: CtaAction;
	label?: ReactNode;
	title?: ReactNode;
	description?: ReactNode;
	image?: SanityImage;
	backgroundImage?: SanityImage;
	backgroundVideo?: string;
	textPosition?: (typeof noneLeftRightValues)[number];
};

export function CTA(props: CTAProps) {
	const textPosition = props.textPosition ?? (props.image ? 'right' : 'none');
	const { base, contentBase, content, button } = styles({ textPosition });

	const [open, setOpen] = useState(false);

	return (
		<>
			<section className={props.className} data-component='CTA'>
				<Container className={base()}>
					<div className={contentBase()}>
						{(props.backgroundImage || props.backgroundVideo) && (
							<div className={`absolute h-full top-0 left-0 w-full -z-1 pointer-events-none`}>
								<Media
									className={`aspect-video h-full w-full max-w-full max-h-full [&_img]:h-full`}
									objectFit='cover'
									image={props.backgroundImage}
									video={props.backgroundVideo}
									priority
								/>
							</div>
						)}

						{textPosition !== 'none' && (
							<Media className={'aspect-square flex items-center overflow-hidden [&_img]:h-full md:flex-[0_0_45%]'} image={props.image} />
						)}

						<div className={content()}>
							{props.label && (
								<Text className='text-text-brand' styleType='label-md'>
									{props.label}
								</Text>
							)}

							{props.title && (
								<Text as='h2' className='text-text-primary' styleType='heading-xl'>
									{props.title}
								</Text>
							)}

							{props.description && (
								<Text className='text-text-secondary' styleType='body-lg'>
									{props.description}
								</Text>
							)}

							{props.action.type === 'link' && props.label && (
								<ButtonLink className={button()} parent='CTA' href={props.action.href}>
									{props.label}
								</ButtonLink>
							)}

							{props.action.type === 'calendly' && props.label && (
								<Button
									className={button()}
									parent='CTA'
									onClick={() => {
										setOpen(true);
									}}
								>
									{props.label}
								</Button>
							)}

							{props.action.type === 'hubspotForm' && (
								<Container size='sm'>
									<HubspotFormCtaAction {...props.action} />
								</Container>
							)}
						</div>
					</div>
				</Container>
			</section>

			{props.action.type === 'calendly' && <CalendlyCtaActionPopup open={open} setOpen={setOpen} {...props.action} />}
		</>
	);
}
